import type { TableColumn, ViewComponent, ViewDefinition } from './viewTypes'

// AB#3953 / AB#3954 — table column personalisation: the PURE half. Nothing in this module touches
// React or a store, so every reconciliation rule is unit-testable in isolation (columnLayout.test.ts)
// and the hook (useTableLayout.ts) and the UI (ColumnConfigurator.tsx) are thin over it.
//
// The contract it builds on is AB#3952's: `list.columns` (default visible, default order),
// `list.availableColumns` (hidden by default), a column's identity is its `field`.

/**
 * AB#3954 — what is stored per (user, table): the fields the user shows, in their order, and the
 * DEFAULT fields the user hid. Both lists are needed: a default column absent from both is one the
 * module added AFTER the layout was saved (a new default), and it must appear — silently dropping
 * every column a module adds until each user rebuilds their layout is the failure this shape prevents.
 */
export interface StoredColumnLayout {
    version: 1
    visible: string[]
    hidden: string[]
}

export const EMPTY_COLUMNS: readonly TableColumn[] = Object.freeze([])

/**
 * AB#3953 — the identity a table's personalisation is stored under.
 *
 * A view's own list: `<moduleId>/<viewKey>`. An embedded table (line-items-table, data-table,
 * json-table, related-entity-list): `<moduleId>/<viewKey>#<component.key ?? component.dataBinding>`.
 * A component with neither key nor binding has no identity and gets no configurator at all.
 *
 * `viewKey` falls back to `entityType` exactly as the platform does everywhere else (AB#1648: the
 * universal view key IS the entity type for entity views; HelpGenerationInput applies the same
 * fallback server-side), so a served view that predates the key still personalises stably.
 */
export function tableIdentity(view: Pick<ViewDefinition, 'moduleId' | 'viewKey' | 'entityType'>, component?: Pick<ViewComponent, 'key' | 'dataBinding'>): string | undefined {
    const key = view.viewKey ?? view.entityType
    if (!view.moduleId || !key) { return undefined }
    const base = `${view.moduleId}/${key}`
    if (!component) { return base }
    const name = component.key ?? component.dataBinding
    return name ? `${base}#${name}` : undefined
}

/** True when `value` has the StoredColumnLayout shape (a stored preference is untrusted JSON). */
export function isStoredColumnLayout(value: unknown): value is StoredColumnLayout {
    if (!value || typeof value !== 'object') { return false }
    const v = value as Record<string, unknown>
    const strings = (x: unknown) => Array.isArray(x) && x.every(s => typeof s === 'string')
    return v.version === 1 && strings(v.visible) && strings(v.hidden)
}

/**
 * AB#3954 — the effective visible columns for one table, reconciling the user's stored layout with
 * what the module declares TODAY.
 *
 *  - allowed = the fields of `defaults` + `available`; anything else in the stored document (a field
 *    the module removed or renamed) drops silently.
 *  - no stored layout ⇒ `defaults`, untouched.
 *  - `visible` = the stored visible fields, in stored order, each resolved to its declared column.
 *  - a DEFAULT column the stored document has never seen (in neither list) is a NEW DEFAULT: it is
 *    spliced in at its default position relative to the other defaults — after the nearest preceding
 *    default that is visible, else before the nearest following one, else at the end.
 *  - a stored hidden default stays hidden; an available column the user never added stays hidden.
 *  - an empty result falls back to `defaults` (a layout that hides everything is not a layout).
 */
export function resolveColumns(defaults: readonly TableColumn[], available: readonly TableColumn[], stored: StoredColumnLayout | null | undefined): TableColumn[] {
    if (!stored || !isStoredColumnLayout(stored)) { return [...defaults] }
    const declared = new Map<string, TableColumn>()
    for (const c of [...defaults, ...available]) { if (!declared.has(c.field)) { declared.set(c.field, c) } }
    const known = new Set([...stored.visible, ...stored.hidden])
    const result: TableColumn[] = []
    const seen = new Set<string>()
    for (const field of stored.visible) {
        const column = declared.get(field)
        if (column && !seen.has(field)) { seen.add(field); result.push(column) }
    }
    defaults.forEach((column, index) => {
        if (known.has(column.field) || seen.has(column.field)) { return }
        // A new default: place it relative to the other defaults, in default order.
        let insertAt = -1
        for (let j = index - 1; j >= 0 && insertAt < 0; j--) {
            const at = result.findIndex(c => c.field === defaults[j]!.field)
            if (at >= 0) { insertAt = at + 1 }
        }
        for (let j = index + 1; j < defaults.length && insertAt < 0; j++) {
            const at = result.findIndex(c => c.field === defaults[j]!.field)
            if (at >= 0) { insertAt = at }
        }
        if (insertAt < 0) { insertAt = result.length }
        result.splice(insertAt, 0, column)
        seen.add(column.field)
    })
    return result.length > 0 ? result : [...defaults]
}

/**
 * AB#3954 — the stored shape for a visible column list: the visible fields (only those the module
 * declares, in order) and the DEFAULT fields not among them (so a later regen can tell a hidden
 * default from a new one — see resolveColumns).
 */
export function serializeLayout(defaults: readonly TableColumn[], available: readonly TableColumn[], visible: readonly TableColumn[]): StoredColumnLayout {
    const allowed = new Set([...defaults, ...available].map(c => c.field))
    const shown: string[] = []
    for (const c of visible) { if (allowed.has(c.field) && !shown.includes(c.field)) { shown.push(c.field) } }
    const hidden = defaults.filter(d => !shown.includes(d.field)).map(d => d.field)
    return { version: 1, visible: shown, hidden }
}

/** True when `columns` differs from the declared default set (order counts). */
export function isCustomisedLayout(defaults: readonly TableColumn[], columns: readonly TableColumn[]): boolean {
    if (defaults.length !== columns.length) { return true }
    return defaults.some((d, i) => d.field !== columns[i]?.field)
}
